import Popover, { popoverClasses } from '@mui/material/Popover';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  List,
  ListItem,
  Typography,
} from '@mui/material';
import React, { useCallback, useState } from 'react';
import { styled } from '@mui/material/styles';
import { IconInfoRedesigned } from 'assets/img';
import { LwIconButton } from 'redesign';
import { ShiftInfoProps } from './types';

export function ShiftInfoButton({ id, history }: ShiftInfoProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => setAnchorEl(event.currentTarget),
    [setAnchorEl]
  );
  const handleClose = useCallback(() => setAnchorEl(null), [setAnchorEl]);

  return (
    <>
      <LwIconButton
        iconColorMode="stroke"
        color="secondary"
        onClick={(e?: React.MouseEvent<HTMLButtonElement>) => handleClick(e!)}
        data-testid="history-icon-button"
      >
        <StyledIconInfoRedesigned />
      </LwIconButton>
      <StyledPopover
        id={id}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        onClose={handleClose}
        open={!!anchorEl}
      >
        <StyledHeaderText variant="subtitle1">Geschiedenis</StyledHeaderText>

        <List sx={{ overflowY: 'scroll' }}>
          {history.map((event, i) => (
            <StyledListItem key={i}>
              <Accordion>
                <AccordionSummary>
                  <Typography variant="body2">{event.definition}</Typography>
                </AccordionSummary>
                {event.properties ? (
                  <AccordionDetails>
                    <List>
                      {event.properties.map((property, j) => (
                        <StyledPropeertyItem key={j}>
                          <Typography variant="body2">
                            {property.name}: {property.value}
                          </Typography>
                        </StyledPropeertyItem>
                      ))}
                    </List>
                  </AccordionDetails>
                ) : null}
              </Accordion>
            </StyledListItem>
          ))}
        </List>
      </StyledPopover>
    </>
  );
}

const StyledHeaderText = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  textAlign: 'center',
  borderBottom: `2px solid ${theme.palette.grey[200]}`,
}));

const StyledIconInfoRedesigned = styled(IconInfoRedesigned)(() => ({
  color: 'black',
}));

const StyledPopover = styled(Popover)(({ theme }) => ({
  marginTop: theme.spacing(2),

  [`& .${popoverClasses.paper}`]: {
    boxShadow: theme.shadows[3],
    borderRadius: theme.spacing(1.5),
    maxHeight: theme.spacing(70),
    maxWidth: theme.spacing(80),
    padding: theme.spacing(1),
    display: 'flex',
    flexDirection: 'column',
  },
}));

const StyledListItem = styled(ListItem)(({ theme }) => ({
  flexDirection: 'column',
  alignItems: 'start',
  padding: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.grey[200]}`,
  '&:last-child': {
    borderBottom: 'none',
  },
  borderRadius: theme.spacing(1),
  '&:nth-child(even)': { background: theme.palette.grey[100] },
}));

const StyledPropeertyItem = styled(ListItem)(({ theme }) => ({
  padding: theme.spacing(1 / 4, 0, 0, 2),
}));
